'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useTheme } from '../context/ThemeContext';
import { HomeIcon, XMarkIcon } from '@heroicons/react/24/outline';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { theme, setTheme, isDark } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  const toggleTheme = () => {
    setTheme(isDark ? 'light' : 'dark');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
        isScrolled
          ? 'bg-[var(--bg)] shadow-sm'
          : 'bg-[var(--bg)]/80'
      }`}
    >
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            href="/"
            className="flex flex-col"
          >
            <span className="text-[var(--font-size-base)] font-[var(--font-weight-semibold)] text-[var(--text)] text-sharp">Vivekanand Pandey</span>
          </Link>

          {/* Navigation */}
          <div className="flex items-center space-x-4">
            <nav className="hidden md:flex items-center space-x-6 opacity-100 transition-opacity duration-300">
              <Link
                href="/"
                className="text-[var(--text)] hover:text-[var(--accent-color)] dark:hover:text-[var(--accent-color)] transition-all duration-300 p-2 rounded-full hover:bg-[var(--bg-alt)] dark:hover:bg-[var(--bg-alt)] flex items-center justify-center"
                aria-label="Home"
              >
                <HomeIcon className="w-[var(--font-size-sm)] h-[var(--font-size-sm)]" />
              </Link>
              <Link
                href="/projects"
                className="text-[var(--text)] hover:text-[var(--accent-color)] dark:hover:text-[var(--accent-color)] transition-colors text-[var(--font-size-sm)] leading-none text-sharp"
              >
                Projects
              </Link>
              <Link
                href="/blogs"
                className="text-[var(--text)] hover:text-[var(--accent-color)] dark:hover:text-[var(--accent-color)] transition-colors text-[var(--font-size-sm)] leading-none text-sharp"
              >
                Blog
              </Link>
              <Link
                href="/contact"
                className="text-[var(--text)] hover:text-[var(--accent-color)] dark:hover:text-[var(--accent-color)] transition-colors text-[var(--font-size-sm)] leading-none text-sharp"
              >
                Contact
              </Link>
            </nav>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="text-[var(--text)] hover:text-[var(--accent-color)] dark:hover:text-[var(--accent-color)] transition-all duration-300 p-2 rounded-full hover:bg-[var(--bg-alt)] dark:hover:bg-[var(--bg-alt)] flex items-center justify-center"
              aria-label="Toggle theme"
            >
              {mounted ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-[var(--font-size-sm)] h-[var(--font-size-sm)] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d={isDark 
                      ? "M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
                      : "M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z"
                    }
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-[var(--font-size-sm)] h-[var(--font-size-sm)] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"
                />
              )}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="md:hidden text-[var(--text)] hover:text-[var(--accent-color)] dark:hover:text-[var(--accent-color)] transition-colors p-0.5 text-sharp flex items-center"
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden fixed inset-0 z-[101] transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50"
          onClick={toggleMobileMenu}
        />
        {/* Bottom Sheet Menu */}
        <div
          className={`absolute bottom-0 left-0 right-0 bg-[var(--bg)] dark:bg-[var(--bg)] rounded-t-2xl transition-transform duration-300 ease-in-out ${
            isMobileMenuOpen ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          {/* Handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-12 h-1 bg-[var(--border)] rounded-full" />
          </div>
          <nav className="flex flex-col px-6 py-4 space-y-4">
            <Link
              href="/"
              className="text-[var(--text)] hover:text-[var(--accent-color)] dark:hover:text-[var(--accent-color)] transition-all duration-300 text-[var(--font-size-lg)] font-[var(--font-weight-medium)] flex items-center gap-3 py-2"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <HomeIcon className="w-6 h-6" />
              Home
            </Link>
            <Link
              href="/projects"
              className="text-[var(--text)] hover:text-[var(--accent-color)] dark:hover:text-[var(--accent-color)] transition-all duration-300 text-[var(--font-size-lg)] font-[var(--font-weight-medium)] flex items-center gap-3 py-2"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
              </svg>
              Projects
            </Link>
            <Link
              href="/blogs"
              className="text-[var(--text)] hover:text-[var(--accent-color)] dark:hover:text-[var(--accent-color)] transition-all duration-300 text-[var(--font-size-lg)] font-[var(--font-weight-medium)] flex items-center gap-3 py-2"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
              </svg>
              Blog
            </Link>
            <Link
              href="/contact"
              className="text-[var(--text)] hover:text-[var(--accent-color)] dark:hover:text-[var(--accent-color)] transition-all duration-300 text-[var(--font-size-lg)] font-[var(--font-weight-medium)] flex items-center gap-3 py-2"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
              Contact
            </Link>
          </nav>
          {/* Bottom padding for safe area */}
          <div className="h-6" />
        </div>
      </div>
    </header>
  );
} 